import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ElementRef,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PickListModule } from 'primeng/picklist';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PedidosService } from 'src/app/services/pedidos.service';
import { ProductCatalogCacheService } from 'src/app/services/product-catalog-cache.service';
import { Produto, ProdutoShopify, transformProdutoShopify } from 'src/app/models/product-picklist.model';

// Componentizado a partir de slick-detail.component.ts — encapsula a picklist
// de associação de produtos (busca remota + paginação por cursor + drag/drop
// desktop e mobile + fix de z-index durante o drag, já que a picklist vive
// dentro de uma sidebar). `target` segue a MESMA semântica de antes: é a
// referência que o <p-pickList> mutata direto (o PrimeNG já lida com mover
// itens entre source/target internamente) — quem chama esse componente
// continua responsável por reatribuir a própria referência de target
// (ex: `this.targetProducts = [...this.targetProducts]`) pra disparar CD,
// exatamente como já fazia antes da componentização.
//
// `source`/`isLoadingMore` são signals (não campos comuns): esse componente é
// destruído e recriado toda vez que o usuário troca de aba (mananger/
// activeOption usam *ngIf no slick-detail), então as respostas assíncronas
// de rede precisam refletir na tela sem depender do zone.js "acordar" um
// componente ancestral OnPush (slick-detail) — signals disparam a checagem
// da própria view independente disso. O catálogo em si (allStoreProducts)
// fica cacheado em ProductCatalogCacheService, que SOBREVIVE a essa
// destruição/recriação — sem isso a lista sumia e recarregava do zero toda
// vez que o usuário voltava pra essa aba.
@Component({
  standalone: true,
  selector: 'app-product-picklist',
  templateUrl: './product-picklist.component.html',
  styleUrls: ['./product-picklist.component.scss'],
  imports: [CommonModule, FormsModule, PickListModule, InputTextModule, SkeletonModule],
})
export class ProductPicklistComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() target: Produto[] = [];
  @Input() isMobile = false;
  @Input() itemTemplate?: TemplateRef<any>;
  @Input() targetHeaderTemplate?: TemplateRef<any>;

  @Output() moveToTarget = new EventEmitter<any>();
  @Output() moveToSource = new EventEmitter<any>();
  // Emitido depois da 1ª checagem de apps de loja — true quando nem Shopify
  // nem Nuvemshop estão conectados. Quem usa o componente decide como avisar
  // o usuário (o banner de "instalar app" é específico de cada tela).
  @Output() integrationRequired = new EventEmitter<boolean>();

  source = signal<Produto[]>([]);
  isLoadingMore = signal(false);
  sourceProductsCount = computed(() => this.source().filter(p => !p.__loading).length);

  private allStoreProducts: Produto[] = [];

  remoteFilter = '';
  private searchDebounce: any;
  currentSearchTerm = '';
  nextCursor: string | null = null;
  hasNextPage = false;

  hasShopifyIntegration = false;
  hasNuvemshopIntegration = false;

  // Modo de arraste mobile: só liga [dragdrop] enquanto o usuário segura o
  // ícone de arraste — senão o drag do CDK conflita com o scroll por toque.
  isDragDrop = false;

  private destroy$ = new Subject<void>();

  @ViewChild('productsPickList', { read: ElementRef })
  productsPickListRef?: ElementRef<HTMLElement>;
  private sourceScrollHandler?: (ev: Event) => void;

  constructor(
    private productsService: PedidosService,
    private catalogCache: ProductCatalogCacheService
  ) { }

  ngOnInit(): void {
    // Se já tem catálogo em cache (troca de aba, ou outro slick da mesma
    // loja), popula na hora — sem isso, some tudo até a request nova voltar.
    const storeId = this.getStoreId();
    if (storeId != null && this.catalogCache.hasCacheFor(storeId)) {
      const cached = this.catalogCache.get(storeId)!;
      this.allStoreProducts = cached.products;
      this.nextCursor = cached.nextCursor;
      this.hasNextPage = cached.hasNextPage;
      this.hasShopifyIntegration = cached.hasShopifyIntegration;
      this.hasNuvemshopIntegration = cached.hasNuvemshopIntegration;
      this.refilterSource();
      this.integrationRequired.emit(!cached.hasShopifyIntegration && !cached.hasNuvemshopIntegration);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // O slick/target muda de fora (troca de item, load inicial) — refiltra o
    // catálogo já carregado pra manter source/target sem interseção.
    if (changes['target'] && this.allStoreProducts.length > 0) {
      this.refilterSource();
    }
  }

  ngAfterViewInit(): void {
    // Sempre revalida em background — se veio de cache, o usuário já está
    // vendo algo; isso só atualiza silenciosamente se algo mudou.
    this.carregaProdutos();
    setTimeout(() => this.attachSourceScrollListener(), 0);
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchDebounce);
    this.detachSourceScrollListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getStoreId(): number | null {
    const raw = localStorage.getItem('ss_SelectedStoreId');
    return raw ? Number(raw) : null;
  }

  private getProductKey(p: any): string {
    return String(p?.sku ?? p?.id);
  }

  private refilterSource(): void {
    this.source.set(
      this.allStoreProducts.filter((p: any) =>
        !this.target.some((t: any) => this.getProductKey(t) === this.getProductKey(p))
      )
    );
  }

  private hasActiveStoreApp(response: any): boolean {
    const apps = response?.store_apps;
    if (!Array.isArray(apps) || !apps.length) {
      return false;
    }
    return apps.some((app: any) => {
      const status = (app?.active ?? app?.status ?? '').toString().toLowerCase();
      return status === 'active' || status === '1' || status === 'true';
    });
  }

  async carregaProdutos(searchTerm: string = ''): Promise<void> {
    this.currentSearchTerm = searchTerm;

    try {
      const [shopifyResponse, nuvemshopResponse] = await Promise.all([
        this.productsService.getAppsStore('shopify').toPromise(),
        this.productsService.getAppsStore('nuvemshop').toPromise(),
      ]);

      this.hasShopifyIntegration = this.hasActiveStoreApp(shopifyResponse);
      this.hasNuvemshopIntegration = this.hasActiveStoreApp(nuvemshopResponse);

      if (!this.hasShopifyIntegration && !this.hasNuvemshopIntegration) {
        this.integrationRequired.emit(true);
        return;
      }
      this.integrationRequired.emit(false);

      const activeKey = this.hasShopifyIntegration ? 'shopify' : 'nuvemshop';
      const response: any = this.hasShopifyIntegration ? shopifyResponse : nuvemshopResponse;

      const storeId = this.getStoreId();

      const storeApp = response.store_apps?.find((app: any) =>
        app.key.includes(activeKey) && app.store_id === storeId
      );

      if (storeApp && String(storeApp.active) === 'active') {
        const observable = await this.productsService.listProducts(null, searchTerm);

        observable.pipe(takeUntil(this.destroy$)).subscribe({
          next: (resp: any) => {
            const allProducts = resp.products.map((product: ProdutoShopify) =>
              transformProdutoShopify(product)
            );

            const changed = JSON.stringify(allProducts) !== JSON.stringify(this.allStoreProducts);

            this.nextCursor = resp.page_info?.end_cursor ?? null;
            this.hasNextPage = !!resp.page_info?.has_next_page;

            if (changed) {
              this.allStoreProducts = allProducts;
              this.refilterSource();
            }

            // Só cacheia o catálogo "default" (sem termo de busca) — resultado
            // de busca é transitório, não vale a pena persistir.
            if (!searchTerm && storeId != null) {
              this.catalogCache.set(storeId, {
                products: this.allStoreProducts,
                nextCursor: this.nextCursor,
                hasNextPage: this.hasNextPage,
                hasShopifyIntegration: this.hasShopifyIntegration,
                hasNuvemshopIntegration: this.hasNuvemshopIntegration,
              });
            }
          },
          error: (err: any) => {
            console.error('Erro ao carregar produtos:', err);
          },
        });
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do app:', error);
    }
  }

  onSourceFilterChange(term: string): void {
    const value = (term || '').trim();
    this.remoteFilter = value;

    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.carregaProdutos(value);
    }, 400);
  }

  private loadingSentinel: Produto = {
    id: -1,
    sku: -1,
    name: '',
    status: 0,
    inputQty: 0,
    position: 0,
    price: '0',
    compare_at_price: null,
    onsale_price: '0',
    price_exchange: '0',
    thumbnail: '',
    __loading: true,
  };

  private addLoadingPlaceholder(): void {
    if (!this.source().some(p => p.__loading)) {
      this.source.update(current => [...current, this.loadingSentinel]);
      this.scrollSourceListToBottom();
    }
  }

  private removeLoadingPlaceholder(): void {
    this.source.update(current => current.filter(p => !p.__loading));
  }

  async loadMoreProducts(): Promise<void> {
    if (!this.hasNextPage || this.isLoadingMore()) return;

    this.isLoadingMore.set(true);
    this.addLoadingPlaceholder();
    try {
      const observable = await this.productsService.listProducts(this.nextCursor, this.currentSearchTerm);

      observable.pipe(takeUntil(this.destroy$)).subscribe({
        next: (resp: any) => {
          this.removeLoadingPlaceholder();

          const newProducts = resp.products.map((product: ProdutoShopify) =>
            transformProdutoShopify(product)
          );

          const uniqueNew = newProducts.filter((np: any) => {
            const key = this.getProductKey(np);
            return !this.source().some(sp => this.getProductKey(sp) === key) &&
              !this.target.some(tp => this.getProductKey(tp) === key);
          });

          this.source.update(current => [...current, ...uniqueNew]);
          this.allStoreProducts = [...this.allStoreProducts, ...uniqueNew];

          this.nextCursor = resp.page_info?.end_cursor ?? null;
          this.hasNextPage = !!resp.page_info?.has_next_page;
          this.isLoadingMore.set(false);

          const storeId = this.getStoreId();
          if (!this.currentSearchTerm && storeId != null) {
            this.catalogCache.appendProducts(storeId, uniqueNew, this.nextCursor, this.hasNextPage);
          }
        },
        error: (err: any) => {
          console.error('Erro ao carregar mais produtos:', err);
          this.removeLoadingPlaceholder();
          this.isLoadingMore.set(false);
        },
      });
    } catch (e) {
      console.error('Erro loadMoreProducts:', e);
      this.removeLoadingPlaceholder();
      this.isLoadingMore.set(false);
    }
  }

  private getSourceListElement(): HTMLElement | null {
    if (!this.productsPickListRef) return null;
    const host = this.productsPickListRef.nativeElement;
    return (
      (host.querySelector('.p-picklist-source .p-picklist-list') as HTMLElement) ||
      (host.querySelector('.p-picklist-list') as HTMLElement) ||
      null
    );
  }

  private attachSourceScrollListener(): void {
    if (!this.productsPickListRef) return;

    const sourceList = this.getSourceListElement();
    if (!sourceList) {
      console.warn('Source list da PickList não encontrada');
      return;
    }

    this.sourceScrollHandler = (event: Event) => this.onSourceScroll(event);
    sourceList.addEventListener('scroll', this.sourceScrollHandler);
  }

  private detachSourceScrollListener(): void {
    if (!this.sourceScrollHandler) return;
    const sourceList = this.getSourceListElement();
    if (sourceList) {
      sourceList.removeEventListener('scroll', this.sourceScrollHandler);
    }
    this.sourceScrollHandler = undefined;
  }

  private scrollSourceListToBottom(): void {
    const sourceList = this.getSourceListElement();
    if (!sourceList) return;

    setTimeout(() => {
      sourceList.scrollTop = sourceList.scrollHeight;
    }, 0);
  }

  private onSourceScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const padding = 30;
    const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - padding;
    if (isNearBottom) {
      this.loadMoreProducts();
    }
  }

  dragDrop(x: number, event?: Event): void {
    event?.stopPropagation();
    this.isDragDrop = x === 0;
  }
  onTouchStart(event: Event): void {
    this.isDragDrop = true;
  }
  onTouchMove(event: Event): void {
    this.isDragDrop = true;
    if (this.isDragDrop) {
      event.preventDefault(); // Previne o comportamento padrão de scroll
    }
  }
  onTouchEnd(event: Event): void {
    this.isDragDrop = false;
  }

  onMoveToTarget(event: any): void {
    this.moveToTarget.emit(event);
  }
  onMoveToSource(event: any): void {
    this.moveToSource.emit(event);
  }

}
